import { EzEnumeration2 } from '/ezlibrary/enums/EzEnumeration2.js';

/**
 * @class
 * @extends {EzEnum}
 * @description
 * Enumeration that defines EzClockerContext event names
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Import into non-enumeration classes with:
 *     import {
 *         // ... other imported enumerations ...,
 *         EzContextEventName
 *     } from '/ezlibrary/enums/EzEnumerations.js';
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Import into other enumeration classes with:
 *     import { EzContextEventName } from '/ezlibrary/EzClockerContext/EzContextEventName.js';
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 */
export class EzContextEventName extends EzEnumeration2 {
    /**
     * @private @field
     * Stores the singleton instance of this enumeration class.
     * @type {EzEnumTemplate}
     */
    static #ezEnumerationSingleton = null;

    /**
     * @static
     * Static Initialization
     */
    static {
        if (null == this.#ezEnumerationSingleton) {
            this.#ezEnumerationSingleton = new EzContextEventName(
                // Enum property names
                [
                    'UNKNOWN',

                    /*~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
                    | EzClockerContext
                    | /ezlibrary/EzClockerContext/ez-context.js
                    ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~*/
                    'onContextLoading',
                    'onContextReady',

                    /*~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
                    | User Context
                    | /ezlibrary/EzClockerContext/ez-context-module-active-account.js
                    ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~*/
                    'onUserContextReady',
                    'onUserContextUpdated',

                    /*~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
                    | Active Account
                    | /ezlibrary/EzClockerContext/ez-context-module-active-account.js
                    ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~*/
                    'onUserContextModuleReady',
                    'onActiveAccountSelectedPeriodChanged',
                    'onActiveAccountReady',
                    'onActiveAccountSelectedPeriodRefreshed',

                    /*~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
                    | Employer Context
                    ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~*/
                    'onEmployerContextReady',
                    'onEmployerContextClosed',

                    /*~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
                    | Selected Employer Account
                    | /ezlibrary/EzClockerContext/ez-context-module-selected-employer.js
                    ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~*/
                    'onSelectedEmployerAccountReady',
                    'onSelectedEmployerAccountChanged',
                    'onSelectedEmployerAccountClosed',
                    'onSelectedEmployerEmployeesChanged',
                    'onSelectedEmployerEmployeeAccountsUpdated',
                    'onSelectedEmployerLicenseReady',
                    'onSelectedEmployerLicenseUpdated',
                    'onSelectedEmployerIntegerationEmployeesReady',
                    'onSelectedEmployerIntegerationEmployeesUpdated',
                    'onSelectedEmployerAccountJobsReady',
                    'onSelectedEmployerAccountActiveIntegrationsReady',
                    'onSelectedEmployerAccountActiveIntegrationsUpdated',
                    'onSelectedEmployerAccountActiveIntegrationsError',

                    /*~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
                    | Active Employer
                    | /ezlibrary/EzClockerContext/ez-context-module-active-employer.js
                    ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~*/
                    'onActiveEmployerReady',
                    'onActiveEmployerChanged',
                    'onActiveEmployerClosed',

                    /*~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
                    | Manager Context
                    ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~*/
                    'onManagerContextReady',
                    'onManagerContextChanged',

                    /*~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
                    | Payroll Manager Context
                    | /ezlibrary/EzClockerContext/ez-context-module-payroll-manager-context.js
                    ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~*/
                    'onPayrollManagerContextReady',
                    'onPayrollManagerContextChanged',

                    /*~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
                    | Employee Context
                    | /ezlibrary/EzClockerContext/ez-context-module-employee-context.js
                    ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~*/
                    'onEmployeeContextReady',
                    'onEmployeeContextChanged',
                    'onEmployeeContextClosed',

                    /*~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
                    | Personal Context
                    | /ezlibrary/EzClockerContext/ez-context-module-personal-context.js
                    ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~*/
                    'onPersonalContextReady',
                    'onPersonalEmployerIdReady',

                    /*~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
                    | Anonymous User Context
                    ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~*/
                    'onAnonymousUserContextReady',
                    'onAnonymousUserContextClosed',

                    /*~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
                    | Active Employee
                    | /ezlibrary/EzClockerContext/ez-context-module-active-employee.js
                    ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~*/
                    'onActiveEmployeeReady',
                    'onActiveEmployeeClosed',
                    'onActiveEmployeeUpdated',
                    'onActiveEmployeeChanged',
                    'onActiveEmployeeTimeEntriesChanged',
                    'onActiveEmployeeSelectedPeriodChanged',
                    'onActiveEmployeeActiveClockInChanged',
                    'onActiveEmployeeActiveBreakInChanged',
                    'onActiveEmployeeUserInfoUpdated',
                    'onActiveEmployeeInviteSuccess',
                    'onActiveEmployeeInviteFailure',
                    'onActiveEmployeeSelectedPeriodTotalsReady',
                    'onActiveEmployeeSelectedPeriodTotalsUpdated',

                    /*~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
                    | Subscription Context
                    ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~*/
                    'onSubscriptionContextLoading',
                    'onSubscriptionContextReady',
                    'onSubscriptionContextActiveCreditCardReady',
                    'onSubscriptionContextActiveCreditCardChanged',
                    'onSubscriptionContextBillingAddressReady',
                    'onSubscriptionContextBillingAddressChanged',
                    'onSubscriptionContextBillingInformationReady',
                    'onSubscriptionContextBillingInformationChanged',
                    'onSubscriptionContextActiveSubscriptionReady',
                    'onSubscriptionContextActiveSubscriptionChanged',
                    'onSubscriptionContextAvailablePlansReady',
                    'onSubscriptionContextAvailablePlansChanged',
                    'onSubscriptionContextAvailablePlansModeChanged',
                    'onSubscriptionContextActiveCustomerReady',
                    'onSubscriptionContextActiveCustomerChanged',

                    /*~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
                    | Validation Events
                    ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~*/
                    'ezOnValidateLicenseValid',
                    'ezOnValidateLicenseExpired',
                    'ezOnValidateLicenseError',
                    'ezOnValidateLicenseFreeTrialExpired',
                    'ezOnValidateLicenseViolationError'
                ],
                // Enum property values for each enum property name as an array
                [
                    'UNKNOWN',

                    /*~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
                    | EzClocker Context
                    | /ezlibrary/EzClockerContext/ez-context-module-user-context.js
                    ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~*/
                    // onContextLoading
                    'EzContextLoading',
                    // onContextReady
                    'EzContextLoaded',

                    /*~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
                    | User Context
                    | /ezlibrary/EzClockerContext/ez-context-module-active-account.js
                    ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~*/
                    // onUserContextReady
                    'ezOn_EzUserContext_Ready',
                    // onUserContextUpdated
                    'EzClockerContext_EzUserContext_Updated',


                    /*~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
                    | Active Account
                    | /ezlibrary/EzClockerContext/ez-context-module-active-account.js
                    ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~*/
                    //'onUserContextModuleReady',
                    'ezOn_EzUserContext_ModuleReady',
                    // 'onActiveAccountSelectedPeriodChanged',
                    'EzClockerContext_ActiveAccount_SelectedPeriod_Changed',
                    // onActiveAccountReady
                    'EzClockerContext_ActiveAccount_Ready',
                    // onActiveAccountSelectedPeriodRefreshed
                    'EzClockerContext_SELECTED_PERIOD_REFRESHED',

                    /*~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
                    | Employer Context
                    ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~*/
                    'EzClockerContext_EmployerContext_Ready',
                    'EzClockerContext_EmployerContext_Closed',

                    /*~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
                    | Selected Employer Account
                    | /ezlibrary/EzClockerContext/ez-context-module-selected-employer.js
                    ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~*/
                    // onSelectedEmployerAccountReady
                    'EzClockerContext_SelectedEmployer_Ready',
                    // onSelectedEmployerAccountChanged
                    'EzClockerContext_SelectedEmployer_Changed',
                    // onSelectedEmployerAccountClosed
                    'EzClockerContext_SelectedEmployer_Closed',
                    // onSelectedEmployerEmployeesChanged
                    'EzClockerContext_SelectedEmployer_Employees_Changed',
                    // onSelectedEmployerEmployeeAccountsUpdated
                    'EzClockerContext_SelectedEmployer_Employees_Updated',
                    // onSelectedEmployerLicenseReady
                    'EzClockerContext_SelectedEmployer_License_Ready',
                    // onSelectedEmployerLicenseUpdated
                    'EzClockerContext_SelectedEmployer_License_Updated',
                    // onSelectedEmployerIntegerationEmployeesReady
                    'EzClockerContext_SelectedEmployer_Integration_Employees_Ready',
                    // onSelectedEmployerIntegerationEmployeesUpdated
                    'EzClockerContext_SelectedEmployer_Integration_Employees_Updated',
                    // onSelectedEmployerAccountJobsReady
                    'EzClockerContext_SelectedEmployer_Jobs_Ready',
                    // onSelectedEmployerAccountActiveIntegrationsReady
                    'EzClockerContext_SelectedEmployer_ActiveIntegrations_Ready',
                    // onSelectedEmployerAccountActiveIntegrationsUpdated
                    'EzClockerContext_SelectedEmployer_ActiveIntegrations_Updated',
                    // onSelectedEmployerAccountActiveIntegrationsError
                    'EzClockerContext_SelectedEmployer_ActiveIntegrations_Error',

                    /*~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
                    | Active Employer
                    | /ezlibrary/EzClockerContext/ez-context-module-active-employer.js
                    ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~*/
                    // onActiveEmployerReady
                    'EzClockerContext_ActiveEmployer_Ready',
                    // onActiveEmployerChanged
                    'EzClockerContext_ActiveEmployer_Changed',
                    // onActiveEmployerClosed
                    'EzClockerContext_ActiveEmployer_Closed',

                    /*~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
                    | Manager Context
                    ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~*/
                    'EzClockerContext_ManagerContext_Ready',
                    'EzClockerContext_ManagerContext_Changed',

                     /*~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
                    | Payroll Manager Context
                    | /ezlibrary/EzClockerContext/ez-context-module-payroll-manager-context.js
                    ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~*/
                    'EzClockerContext_PayrollManagerContext_Ready',
                    'EzClockerContext_PayrollManagerContext_Changed',

                    /*~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
                    | Employee Context
                    | /ezlibrary/EzClockerContext/ez-context-module-employee-context.js
                    ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~*/
                    'EzClockerContext_EmployeeContext_Ready',
                    // onEmployeeContextChanged
                    'EzClockerContext_EmployeeContext_Changed',
                    'EzClockerContext_EmployeeContext_Closed',

                    /*~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
                    | Personal Context
                    | /ezlibrary/EzClockerContext/ez-context-module-personal-context.js
                    ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~*/
                    'EzClockerContext_PersonalContext_Ready',
                    'EzClockerContext_OnPersonalEmployerId_Ready',

                    //'EzClockerContext_UserContext_Ready',

                    /*~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
                    | Anonymous User Context
                    ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~*/
                    'EzClockerContext_OnAnonymousUser_Context_Ready',
                    'EzClockerContext_OnAnonymousUser_Context_Closed',

                    /*~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
                    | Active Employee
                    | /ezlibrary/EzClockerContext/ez-context-module-active-employee.js
                    ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~*/
                    'EzClockerContext_ActiveEmployee_Ready',
                    'EzClockerContext_ActiveEmployee_Closed',
                    'EzClockerContext_ActiveEmployee_Updated',
                    'EzClockerContext_ActiveEmployee_Changed',
                    'EzClockerContext_ActiveEmployee_TimeEntries_Changed',
                    'EzClockerContext_ActiveEmployee_SelectedPeriod_Changed',
                    'EzClockerContext_ActiveEmployee_ActiveClockIn_Changed',
                    'EzClockerContext_ActiveEmployee_ActiveBreakIn_Changed',
                    'EzClockerContext_ActiveEmployee_UserInfo_Updated',
                    'EzClockerContext_ActiveEmployee_Invite_Success',
                    'EzClockerContext_ActiveEmployee_Invite_Failure',
                    'EzClockerContext_ActiveEmployee_SelectedPeriod_Totals_Ready',
                    'EzClockerContext_ActiveEmployee_SelectedPeriod_Totals_Update',

                    /*~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
                    | Subscription Context
                    ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~*/
                    'EzClockerContext_SubscriptionContext_Loading',
                    'EzClockerContext_SubscriptionContext_Ready',
                    'EzClockerContext_SubscriptionContext_ActiveCreditCard_Ready',
                    'EzClockerContext_SubscriptionContext_ActiveCreditCard_Changed',
                    'EzClockerContext_SubscriptionContext_BillingAddress_Ready',
                    'EzClockerContext_SubscriptionContext_BillingAddress_Changed',
                    'EzClockerContext_SubscriptionContext_BillingInformation_Ready',
                    'EzClockerContext_SubscriptionContext_BillingInformation_Changed',
                    'EzClockerContext_SubscriptionContext_ActiveSubscription_Ready',
                    'EzClockerContext_SubscriptionContext_ActiveSubscription_Changed',
                    'EzClockerContext_SubscriptionContext_AvailablePlans_Ready',
                    'EzClockerContext_SubscriptionContext_AvailablePlans_Changed',
                    'EzClockerContext_SubscriptionContext_AvailablePlans_Mode_Changed',
                    'EzClockerContext_SubscriptionContext_ActiveCustomer_Ready',
                    'EzClockerContext_SubscriptionContext_ActiveCustomer_Changed',

                    /*~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
                    | Validation Events
                    ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~*/
                    'ezOnValidateLicenseValid',
                    'ezOnValidateLicenseExpired',
                    'ezOnValidateLicenseError',
                    'ezOnValidateLicenseFreeTrialExpired',
                    'ezOnValidateLicenseViolationError'
                ],
                // Optional array of additional data for eac enumeration property.
                // Enter null if not needed.
                null);
        }
    }
}
